import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { BellRing, Mail, TestTube2, AlertTriangle, Save, Power, PowerOff } from "lucide-react";

const inp = "w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-orange/60";

export const AdSenseAlertsSection = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/admin/adsense/alerts").then(({ data }) => setCfg(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!cfg) return null;

  const update = (k, v) => setCfg({ ...cfg, [k]: v });

  const save = async () => {
    if (cfg.recipient && !/^\S+@\S+\.\S+$/.test(cfg.recipient)) { toast.error("Email invalide"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/adsense/alerts", {
        enabled: cfg.enabled, recipient: cfg.recipient,
        earn_drop_pct: +cfg.earn_drop_pct, imp_drop_pct: +cfg.imp_drop_pct, ctr_spike_pct: +cfg.ctr_spike_pct,
      });
      setCfg(data.config);
      toast.success("Alertes enregistrées");
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!cfg.recipient) { toast.error("Saisis un email destinataire et enregistre d'abord"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/adsense/alerts/test");
      if (data.ok) toast.success(`Email de test envoyé à ${data.sent_to}`);
      else toast.error("Email non envoyé (vérifie config Resend)");
      if (data.anomalies_detected > 0) toast.warning(`${data.anomalies_detected} anomalie(s) actuellement détectée(s)`);
    } catch (e) { toast.error(e?.response?.data?.error || "Erreur"); }
    finally { setTesting(false); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-5" data-testid="adsense-alerts-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="font-display text-lg font-black inline-flex items-center gap-2"><BellRing size={16} className="text-brand-orange" /> Alertes intelligentes</span>
          <span className="text-[10px] text-white/45 uppercase tracking-widest">via Resend</span>
        </div>
        <button
          onClick={() => { update("enabled", !cfg.enabled); }}
          className={`px-3 py-1.5 rounded-xl text-xs font-bold inline-flex items-center gap-2 transition ${
            cfg.enabled
              ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
              : "bg-white/5 text-white/60 border border-white/10 hover:bg-white/10"
          }`}
          data-testid="alerts-toggle"
        >
          {cfg.enabled ? <><Power size={12} /> Activées</> : <><PowerOff size={12} /> Désactivées</>}
        </button>
      </div>

      <p className="text-sm text-white/60">
        Reçois un email quand tes revenus AdSense chutent anormalement OU quand le CTR explose (signe potentiel de fraude).
        Le cron se lance 1x / jour, avec un cooldown de 12h pour éviter le spam.
      </p>

      <div className="space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-white/45 font-bold block mb-1 inline-flex items-center gap-1"><Mail size={10} /> Email destinataire</label>
          <input type="email" value={cfg.recipient || ""} onChange={(e) => update("recipient", e.target.value)} placeholder="ton-email@exemple.com" className={inp} data-testid="alerts-recipient" />
        </div>

        <details className="rounded-xl border border-white/10 bg-black/30 p-3">
          <summary className="cursor-pointer text-xs uppercase tracking-widest text-white/55 font-bold">Seuils de déclenchement</summary>
          <div className="grid md:grid-cols-3 gap-3 mt-3">
            <Threshold label="Chute revenus" value={cfg.earn_drop_pct} onChange={(v) => update("earn_drop_pct", v)} suffix="%" hint="vs moy. 7j" testid="th-earn" />
            <Threshold label="Chute impressions" value={cfg.imp_drop_pct} onChange={(v) => update("imp_drop_pct", v)} suffix="%" hint="vs moy. 7j" testid="th-imp" />
            <Threshold label="Pic CTR" value={cfg.ctr_spike_pct} onChange={(v) => update("ctr_spike_pct", v)} suffix="%" hint="au-dessus moy." testid="th-ctr" />
          </div>
        </details>

        {cfg.last_alert_sent && (
          <p className="text-[11px] text-white/40 inline-flex items-center gap-1">
            <AlertTriangle size={10} /> Dernière alerte envoyée le {new Date(cfg.last_alert_sent.replace(" ", "T") + "Z").toLocaleString("fr-FR")}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={busy} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="alerts-save">
          <Save size={14} /> {busy ? "Enregistrement…" : "Enregistrer"}
        </button>
        <button onClick={sendTest} disabled={testing} className="btn-ghost text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="alerts-test">
          <TestTube2 size={14} /> {testing ? "Envoi…" : "Envoyer un test"}
        </button>
      </div>

      <details className="rounded-xl border border-white/10 bg-black/30 p-3 text-xs text-white/65">
        <summary className="cursor-pointer text-white/55 font-bold uppercase tracking-widest">Configuration cron OVH (1x/jour)</summary>
        <p className="mt-2">Sur OVH → Hébergements → Tâches automatisées → <strong>Ajouter une tâche</strong> :</p>
        <pre className="bg-black/60 rounded-lg p-2 mt-2 text-[11px] overflow-x-auto">wget -q -O - "https://www.voamada.com/api/cron/adsense-alerts?key=&lt;CRON_KEY&gt;"</pre>
        <p className="mt-1">Fréquence : tous les jours à 09:00. La <code>CRON_KEY</code> doit être ajoutée à <code>api/config.php</code> sur OVH (clé <code>cron_key</code>).</p>
      </details>
    </div>
  );
};

const Threshold = ({ label, value, onChange, suffix, hint, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-white/45 font-bold block mb-1">{label}</label>
    <div className="flex items-center gap-1">
      <input type="number" min="1" max="500" value={value} onChange={(e) => onChange(e.target.value)} className={inp + " !flex-1 font-mono"} data-testid={testid} />
      <span className="text-white/45 text-sm">{suffix}</span>
    </div>
    <p className="text-[10px] text-white/35 mt-0.5">{hint}</p>
  </div>
);

export default AdSenseAlertsSection;
